import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { ChevronRight, Calendar, Car, ArrowRight } from 'lucide-react';
import { useTranslation } from '../LanguageContext';

const WikiModelPage = () => {
  const { t } = useTranslation();
  const { modelId } = useParams();
  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchModel = async () => {
      try {
        const response = await axios.get(`${API}/wiki/models/${modelId}`);
        setModel(response.data);
      } catch (error) {
        console.error('Error fetching model:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchModel();
  }, [modelId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!model) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-heading text-2xl font-bold mb-4">{t('wikiPage.modelNotFound')}</h2>
          <Link to="/wiki" className="text-cyan-500 hover:underline">
            {t('wikiPage.backToWiki')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-400 mb-8">
          <Link to="/wiki" className="hover:text-cyan-500 transition-colors">Wiki</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to={`/wiki/${model.brand}`} className="hover:text-cyan-500 transition-colors">{model.brand}</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-white">{model.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Image */}
          <div className="rounded-2xl overflow-hidden glass-card">
            <div className="aspect-[4/3] relative">
              <img 
                src={model.image_url} 
                alt={`${model.brand} ${model.name}`}
                className="w-full h-full object-cover car-image-stylized"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
            </div>
          </div>

          {/* Details */}
          <div className="space-y-6">
            <div>
              <p className="text-sm text-cyan-500 uppercase tracking-wider mb-2">{model.brand}</p>
              <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">{model.name}</h1>
              <p className="text-gray-400 text-lg leading-relaxed">{model.description}</p>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="glass-card rounded-xl p-4">
                <p className="text-sm text-gray-500 mb-1">{t('wikiPage.productionYears')}</p>
                <p className="font-heading text-lg font-semibold flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-cyan-500" />
                  {model.years[0]} - {model.years[model.years.length - 1] || 'Present'}
                </p>
              </div>
              <div className="glass-card rounded-xl p-4">
                <p className="text-sm text-gray-500 mb-1">{t('wikiPage.bodyTypes')}</p>
                <p className="font-heading text-lg font-semibold flex items-center gap-2">
                  <Car className="w-4 h-4 text-cyan-500" />
                  {model.body_types.join(', ')}
                </p>
              </div>
            </div>

            {/* Years Timeline */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="font-heading text-lg font-semibold mb-4">{t('wikiPage.productionTimeline')}</h3>
              <div className="flex flex-wrap gap-2">
                {model.years.slice(-20).map(year => (
                  <span 
                    key={year}
                    className="px-3 py-1 rounded-lg bg-cyan-500/10 text-cyan-500 text-sm font-mono"
                  >
                    {year}
                  </span>
                ))}
                {model.years.length > 20 && (
                  <span className="px-3 py-1 rounded-lg bg-white/5 text-gray-500 text-sm">
                    +{model.years.length - 20} {t('wikiPage.moreYears')}
                  </span>
                )}
              </div>
            </div>

            {/* CTA */}
            <Link to={`/cars?brand=${model.brand}`}>
              <Button className="w-full h-14 bg-cyan-500 text-black hover:bg-cyan-400 font-bold text-lg shadow-neon hover:shadow-neon-lg transition-all">
                {model.brand} {model.name} {t('wikiPage.viewForSale')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WikiModelPage;
